import { Group, Stack, Text, TextInput } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { zod4Resolver } from 'mantine-form-zod-resolver';
import { IconCalendarEvent } from '@tabler/icons-react';
import {
  intakeDemographicsSchema,
  type IntakeDemographicsInput,
} from '@/schemas/intake-demographics.schema';
import type { IntakeAnswers } from '../types';
import type { IntakeAction } from '../types';
import { dateInputValueToYyyyMmDd, parseDateOnlyLocal } from '../intakeDateUtils';

interface StepZipProps {
  answers: IntakeAnswers;
  dispatch: React.Dispatch<IntakeAction>;
  /** Override default intake intro copy (e.g. staff manual add-client). */
  introText?: string;
}

export function StepZip({ answers, dispatch, introText }: StepZipProps): JSX.Element {
  const form = useForm<IntakeDemographicsInput>({
    mode: 'controlled',
    initialValues: {
      firstName: answers.firstName,
      lastName: answers.lastName,
      email: answers.email,
      dateOfBirth: answers.dateOfBirth,
      zip: answers.zip,
    },
    validate: zod4Resolver(intakeDemographicsSchema),
    validateInputOnBlur: true,
    validateInputOnChange: true,
  });

  return (
    <Stack gap="md">
      <Text size="sm" c="dimmed">
        {introText ??
          "We'll use this to find providers near you or in your state for virtual care."}
      </Text>
      <Group grow align="flex-start">
        <TextInput
          label="First name"
          placeholder="First name"
          required
          value={form.values.firstName}
          error={form.errors.firstName}
          onChange={(e) => {
            const value = e.currentTarget.value;
            form.setFieldValue('firstName', value);
            dispatch({ type: 'SET_FIRST_NAME', payload: value });
          }}
        />
        <TextInput
          label="Last name"
          placeholder="Last name"
          required
          value={form.values.lastName}
          error={form.errors.lastName}
          onChange={(e) => {
            const value = e.currentTarget.value;
            form.setFieldValue('lastName', value);
            dispatch({ type: 'SET_LAST_NAME', payload: value });
          }}
        />
      </Group>
      <TextInput
        label="Email"
        placeholder="Email address"
        type="email"
        required
        value={form.values.email}
        error={form.errors.email}
        onChange={(e) => {
          const value = e.currentTarget.value;
          form.setFieldValue('email', value);
          dispatch({ type: 'SET_EMAIL', payload: value });
        }}
      />
      <DateInput
        label="Date of birth"
        placeholder="Select date"
        required
        value={form.values.dateOfBirth ? parseDateOnlyLocal(form.values.dateOfBirth) : null}
        error={form.errors.dateOfBirth}
        onChange={(v) => {
          const value = dateInputValueToYyyyMmDd(v);
          form.setFieldValue('dateOfBirth', value);
          dispatch({ type: 'SET_DATE_OF_BIRTH', payload: value });
        }}
        maxDate={new Date()}
        leftSection={<IconCalendarEvent size={16} />}
        clearable
      />
      <TextInput
        label="ZIP code"
        placeholder="ZIP code"
        required
        value={form.values.zip}
        error={form.errors.zip}
        onChange={(e) => {
          const value = e.currentTarget.value.replace(/\D/g, '').slice(0, 5);
          form.setFieldValue('zip', value);
          dispatch({ type: 'SET_ZIP', payload: value });
        }}
        maxLength={5}
      />
    </Stack>
  );
}
